import React from 'react';

import { cn } from '@/lib/utils';

import { ButtonProps } from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import { LinkButtonProps } from '@/components/atoms/LinkButton';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import { slugify } from '@/cms/utils';
import { sanitizeSideNavText } from '@/features/blog/SideNavigation/utils';
import { StrapiTheme } from '@/theme/themeProviderUtils';

export const BLOCK_BODY_CONTENT_ID = 'block-body-content';

export type BlockHeader = Pick<
  BlockHeaderContentProps,
  'title' | 'subtitle' | 'buttonPosition' | 'buttons'
> & {
  theme?: StrapiTheme;
};

export function generateCMSButtonsForBlockHeader(
  buttons: LinkButtonStrapiProps[]
): ((props: object) => React.ReactNode)[] {
  return buttons.map((button) => {
    return (props: object) => (
      <LinkButtonStrapi
        key={button.text + button.url}
        block={{
          ...button,
          variant: button.variant ?? 'outlined',
          color: button.color ?? 'secondary',
        }}
        inner={props}
      />
    );
  });
}

interface BlockHeaderContentProps {
  title?: React.ReactNode;
  titleLevel?: 'h1' | 'h2' | 'h3';
  /** Overrides the auto-slugified header id (needed when title is a React node) */
  id?: string;
  subtitle?: React.ReactNode | RichTextFieldStrapiProps['text'];
  renderButtons?: ((props: object) => React.ReactNode)[];
  buttonPosition?: 'top' | 'bottom';
  alignment?: 'left' | 'center';
  classNames?: {
    root?: string;
    subtitle?: string;
    children?: string;
    title?: string;
    header?: string;
    button?: string;
  };
  children?: React.ReactNode;
  bottomChildren?: React.ReactNode;
  topChildren?: React.ReactNode;
  // strapi specific
  buttons?: LinkButtonStrapiProps[];
  /**
   * Layout-specific configurations that don't fit into other prop categories.
   */
  layoutOptions?: {
    /**
     * If true, constrains the content width to 1280px on desktop and adds horizontal padding.
     * Intended to match a layout similar to a 1360px container with 60px side padding.
     */
    constrainWidthWithPadding?: boolean;
  };
}

export const blockHeaderTopSpacingClasses = 'mb-8 md:mb-16 ';
export const blockHeaderBottomSpacingClasses = 'mt-8 md:mt-16';
export const blockContentBaseClasses = 'flex flex-wrap w-full gap-6';
export const blockContentAlignClasses = {
  center:
    'md:text-center md:justify-center md:items-center text-left items-center justify-start',
  left: 'text-left md:justify-start md:items-start items-center justify-start',
};

export function handleRenderButtons(
  renderButtons: BlockHeaderContentProps['renderButtons']
) {
  if (!renderButtons) return null;

  return renderButtons.map((renderButton) =>
    renderButton({
      classNames: { root: 'w-full md:w-auto' },
    } satisfies Partial<ButtonProps | LinkButtonProps>)
  );
}
const defaultButtonPosition: BlockHeaderContentProps['buttonPosition'] =
  'bottom';

export function BlockHeaderContent({
  title,
  titleLevel = 'h2',
  id,
  subtitle,
  renderButtons,
  buttons,
  buttonPosition = defaultButtonPosition,
  alignment,
  classNames,
  children,
  bottomChildren,
  topChildren,
  layoutOptions,
}: BlockHeaderContentProps) {
  const buttonRenderPosition = buttonPosition || defaultButtonPosition;

  let extendedRenderButton = generateCMSButtonsForBlockHeader(buttons ?? []);
  if (renderButtons && renderButtons.length > 0) {
    extendedRenderButton = [...renderButtons, ...extendedRenderButton];
  }

  const selectedAlignmentClasses =
    blockContentAlignClasses[alignment ?? 'center'];

  const headerId =
    id ??
    slugify(typeof title === 'string' ? sanitizeSideNavText(title) : '');

  return (
    <div className={cn('flex w-full flex-col', classNames?.root)}>
      {(title ||
        subtitle ||
        topChildren ||
        (!!extendedRenderButton.length && buttonRenderPosition === 'top')) && (
        <div
          id={headerId || undefined}
          className={cn(
            'flex w-full flex-col',
            blockHeaderTopSpacingClasses,
            selectedAlignmentClasses,
            'blockTitle',
            classNames?.header
          )}
        >
          {title && (
            <div className={cn('w-full max-w-5xl', classNames?.title)}>
              {React.isValidElement(title) ? (
                title
              ) : (
                <Heading level={titleLevel ?? 'h2'}>{title}</Heading>
              )}
            </div>
          )}
          {subtitle && (
            <div
              className={cn(
                'mt-5 w-full max-w-5xl text-gray-500 lg:mt-6',
                classNames?.subtitle
              )}
            >
              {React.isValidElement(subtitle) ? (
                subtitle
              ) : (
                <RichTextFieldStrapi
                  block={subtitle as RichTextFieldStrapiProps}
                />
              )}
            </div>
          )}
          {buttonRenderPosition === 'top' &&
            extendedRenderButton.length > 0 && (
              <div
                className={cn(
                  'mt-8 flex w-full flex-col gap-6 md:mt-9 md:flex-row',
                  selectedAlignmentClasses,
                  classNames?.button
                )}
                data-tracker-component='action'
              >
                {handleRenderButtons(extendedRenderButton)}
              </div>
            )}
          {topChildren && (
            <div
              className={cn(
                'mt-8 flex w-full md:mt-9',
                selectedAlignmentClasses
              )}
            >
              {topChildren}
            </div>
          )}
        </div>
      )}
      {children && (
        <div
          id={BLOCK_BODY_CONTENT_ID}
          className={cn(
            blockContentBaseClasses,
            selectedAlignmentClasses,
            'blockBodyContent',
            classNames?.children,
            layoutOptions?.constrainWidthWithPadding && 'mx-auto max-w-[1280px]'
          )}
        >
          {children}
        </div>
      )}
      {buttonRenderPosition === 'bottom' && extendedRenderButton.length > 0 && (
        <div
          className={cn(
            'flex w-full flex-col gap-6 md:flex-row',
            selectedAlignmentClasses,
            blockHeaderBottomSpacingClasses,
            classNames?.button
          )}
          data-tracker-component='action'
        >
          {handleRenderButtons(extendedRenderButton)}
        </div>
      )}
      {bottomChildren && (
        <div className={cn(selectedAlignmentClasses)}>{bottomChildren}</div>
      )}
    </div>
  );
}
